import { FiltersMap } from '@wix/bex-core';
import {
  useWixPatternsContainer,
  useCollectionContext,
} from '@wix/bex-core/react';
import { TextButton, TextButtonProps } from '@wix/design-system';
import React from 'react';
import { Revert } from '@wix/wix-ui-icons-common';
import { ToolbarCollectionEmptyStateBasePrivateProps } from '../components/EmptyState/ToolbarCollectionEmptyStateBasePrivate';
import { EmptyStateBasePrivateProps } from '../components/EmptyState/EmptyStateBase';

interface ToolbarCollectionEmptyStateTextsProps<F extends FiltersMap>
  extends EmptyStateBasePrivateProps {
  state: ToolbarCollectionEmptyStateBasePrivateProps<F>['state'];
  disableDefaultCta?: boolean;
}
export function useToolbarCollectionEmptyStateTexts<F extends FiltersMap>(
  props: ToolbarCollectionEmptyStateTextsProps<F>,
) {
  const { state, disableDefaultCta, title, subtitle, cta } = props;
  const { translate: t } = useWixPatternsContainer();
  const {
    hasAvailableItems,
    hasNonPersistentCustomFilters,
    resultOriginQuerySearch: search,
    resultOriginQueryRawSearch: rawSearch,
  } = state;

  // has search no filter
  if (hasAvailableItems && search && !hasNonPersistentCustomFilters) {
    return {
      title: t('cairo.search.nothingMatches.title', {
        term: rawSearch,
      }),
      subtitle: t('cairo.search.nothingMatches.text'),
      cta: !disableDefaultCta && (
        <ResetFiltersButton>
          {t('cairo.emptyState.reset.button')}
        </ResetFiltersButton>
      ),
    };
  }
  // has filter no search
  if (hasAvailableItems && !search && hasNonPersistentCustomFilters) {
    return {
      title: t('cairo.search.noresults.title'),
      subtitle: t('cairo.emptyState.subtitle.noFilterResults'),
      cta: !disableDefaultCta && (
        <ResetFiltersButton>
          {t('cairo.emptyState.filters.nothingFound.clearFilters.CTA')}
        </ResetFiltersButton>
      ),
    };
  }
  // has filter and search
  if (hasAvailableItems && search && hasNonPersistentCustomFilters) {
    return {
      title: t('cairo.search.nothingMatches.title', {
        term: rawSearch,
      }),
      subtitle: t('cairo.search.filter.nothingMatches.text'),
      cta: !disableDefaultCta && (
        <ResetFiltersButton>
          {t('cairo.emptyState.reset.button')}
        </ResetFiltersButton>
      ),
    };
  }
  // No filter no search - custom
  return {
    title,
    subtitle,
    cta: !disableDefaultCta ? cta : undefined,
  };
}

function ResetFiltersButton(props: TextButtonProps) {
  const state = useCollectionContext();

  return (
    <TextButton
      {...props}
      prefixIcon={<Revert />}
      dataHook="empty-state-clear-filters-button"
      onClick={() => {
        state.emitter.emit('beforeClearFilters', { origin: 'empty-state' });
        state.resetFiltersAndRefresh();
      }}
    />
  );
}
